/**
 * The Proposal tab's approval row — folder 51 Board 10 C's component
 * (`design/mockups/51-change-orders/co-10-recorded-approval.png`, the card
 * under *In the office*), which `docs/job-stages.md` §8 A puts on a quote's
 * rail once the office has approved it:
 *
 * - **Approved by Karen Whitmer — by phone** (14, bold), beside the 30 px
 *   accent tile with its check;
 * - *recorded by Tom Reyes · Tue Oct 27, 4:10 PM* (12, muted);
 * - what was said, quoted, on `surface-alt` (12.5, `text-body`).
 *
 * Only those three parts, as §8 A lists them: the board's change-order
 * figures (*Low-voltage lighting added · +$1,180.46*) and its *Signed copy
 * emailed …* line are folder 51's.
 */
import type { OfficeApprovalRow } from '@rowted/shared'
import { StageGlyph } from '../job-stages/action-glyphs.js'
import { APPROVAL_ROW } from './copy.js'

const weekdayMonthDay = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
const clock = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })

/** *Tue Oct 27* and *4:10 PM*, where the reader is. */
function dayAndTime(iso: string): { day: string; time: string } {
  const at = new Date(iso)
  const parts = weekdayMonthDay.formatToParts(at)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((one) => one.type === type)?.value ?? ''
  return { day: `${part('weekday')} ${part('month')} ${part('day')}`, time: clock.format(at) }
}

export function ApprovalRow({ approval }: { approval: OfficeApprovalRow }) {
  const how = approval.how && approval.how !== 'link' ? APPROVAL_ROW.how[approval.how] : null
  const { day, time } = dayAndTime(approval.approvedAt)
  return (
    <section
      data-approval-row=""
      className="rounded-record border border-line bg-surface px-4 py-3.5 leading-[normal]"
    >
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-button
                         border border-accent-line bg-accent-soft text-accent-dark">
          <StageGlyph name="check" size={15} stroke={1.9} />
        </span>
        <div className="flex min-w-0 flex-col">
          <b className="text-[14px] font-bold text-text">{APPROVAL_ROW.title(approval.name, how)}</b>
          {approval.recordedByName && (
            <span className="text-[12px] text-text-muted">
              {APPROVAL_ROW.recorded(approval.recordedByName, day, time)}
            </span>
          )}
        </div>
      </div>
      {approval.note && (
        <div className="mt-2.5 rounded-field bg-surface-alt px-3 py-2.5 text-[12.5px] text-text-body">
          {APPROVAL_ROW.said(approval.note)}
        </div>
      )}
    </section>
  )
}
